/**
 * A fila da avaliadora (`TP-065`): `GET /fila-da-avaliadora` e o acionamento de um Caso. Mora em `shared/` porque o
 * detalhe do Caso (painel das frentes) e a tela da fila leem a mesma coisa, e uma feature não importa a outra. A situação (aguardando,
 * vencido, aceita) vem calculada do back, contra o relógio dele: a tela não refaz a conta (`D5`).
 */

import { queryOptions } from '@tanstack/react-query';
import type { Schemas } from '@anery/api-contracts';

import { requisitar } from '../api/clienteHttp';
import { parseInstanteOuNulo, type Instante } from '../dominio/datas';
import { RAIZ_DA_FILA_DA_AVALIADORA } from './vencidos';

export type SituacaoDoAcionamento = 'aguardando_aceite' | 'aceite_vencido' | 'aceita';
export type Turno = 'manha' | 'tarde' | 'noite';

export const ROTULO_DA_SITUACAO: Record<SituacaoDoAcionamento, string> = {
  aguardando_aceite: 'Aguardando aceite',
  aceite_vencido: 'Aceite vencido',
  aceita: 'Aceita',
};

export const ROTULO_DO_TURNO: Record<Turno, string> = {
  manha: 'Manhã',
  tarde: 'Tarde',
  noite: 'Noite',
};

export const TURNOS: readonly Turno[] = ['manha', 'tarde', 'noite'];

export interface AcionamentoDaAvaliadora {
  readonly casoId: string;
  readonly paciente: string;
  readonly operadora: string;
  readonly nucleo: string | null;
  readonly modalidade: string | null;
  readonly avaliadora: string | null;
  /** `null`: o Caso foi acionado antes da fila existir, e só o aceite ficou registrado. */
  readonly acionadaEm: Instante | null;
  /** `null`: a curadoria não deu prazo, e o acionamento nunca vence. */
  readonly prazoAceiteEm: Instante | null;
  readonly aceitaEm: Instante | null;
  readonly turno: Turno | null;
  readonly situacao: SituacaoDoAcionamento;
}

export interface FilaDaAvaliadora {
  readonly itens: readonly AcionamentoDaAvaliadora[];
  /** Os vencidos do escopo inteiro, sem o filtro: é o número do menu. */
  readonly vencidos: number;
}

export function mapearAcionamento(f: Schemas['AcionamentoDaAvaliadoraResponse']): AcionamentoDaAvaliadora {
  return {
    casoId: f.casoId,
    paciente: f.paciente ?? '',
    operadora: f.operadora ?? '',
    nucleo: f.nucleo ?? null,
    modalidade: f.modalidade ?? null,
    avaliadora: f.avaliadora ?? null,
    acionadaEm: parseInstanteOuNulo(f.acionadaEm),
    prazoAceiteEm: parseInstanteOuNulo(f.prazoAceiteEm),
    aceitaEm: parseInstanteOuNulo(f.aceitaEm),
    turno: (f.turno ?? null) as Turno | null,
    situacao: (f.situacao ?? 'aguardando_aceite') as SituacaoDoAcionamento,
  };
}

export const chavesDaFilaDaAvaliadora = {
  fila: (situacao: SituacaoDoAcionamento | null) => [...RAIZ_DA_FILA_DA_AVALIADORA, situacao ?? 'todas'] as const,
  doCaso: (casoId: string) => ['casos', casoId, 'acionamento-da-avaliadora'] as const,
};

/** A fila, filtrada ou não. Revalida a cada minuto: o vencido aparece sem a pessoa recarregar. */
export function filaDaAvaliadora(situacao: SituacaoDoAcionamento | null) {
  const busca = situacao === null ? '' : `?situacao=${encodeURIComponent(situacao)}`;
  return queryOptions({
    queryKey: chavesDaFilaDaAvaliadora.fila(situacao),
    queryFn: ({ signal }) =>
      requisitar<Schemas['FilaDaAvaliadoraResponse']>(`/fila-da-avaliadora${busca}`, { sinal: signal }).then(
        (fio): FilaDaAvaliadora => ({ itens: (fio.itens ?? []).map(mapearAcionamento), vencidos: fio.vencidos }),
      ),
    refetchInterval: 60_000,
  });
}

/** O acionamento mais recente do Caso, ou `null` se ele nunca foi acionado (`204`). */
export function acionamentoDoCaso(casoId: string) {
  return queryOptions({
    queryKey: chavesDaFilaDaAvaliadora.doCaso(casoId),
    queryFn: ({ signal }) =>
      requisitar<Schemas['AcionamentoDaAvaliadoraResponse'] | undefined>(
        `/casos/${encodeURIComponent(casoId)}/acionamento-da-avaliadora`,
        { sinal: signal },
      ).then((fio) => (fio === undefined ? null : mapearAcionamento(fio))),
  });
}
